<div class="segy-viewer">
  <!-- ── File picker ───────────────────────────────────────────── -->
  <nz-card nzSize="small" class="picker-card">
    <h2 class="picker-title">SEG-Y file viewer</h2>

    <nz-radio-group
      class="mode-toggle"
      nzSize="small"
      [ngModel]="inputMode()"
      (ngModelChange)="inputMode.set($event)"
      nzButtonStyle="solid"
    >
      <label nz-radio-button nzValue="file">Open file</label>
      <label nz-radio-button nzValue="directory">Browse directory</label>
    </nz-radio-group>

    <div class="picker-row">
      <nz-radio-group
        [ngModel]="isAws()"
        (ngModelChange)="isAws.set($event)"
        nzButtonStyle="solid"
      >
        <label nz-radio-button [nzValue]="true">S3</label>
        <label nz-radio-button [nzValue]="false">Local</label>
      </nz-radio-group>

      @if (inputMode() === "file") {
        <input
          nz-input
          class="path-input"
          [placeholder]="placeholder()"
          [ngModel]="pathInput()"
          (ngModelChange)="pathInput.set($event)"
          (keyup.enter)="load()"
        />

        <button
          nz-button
          nzType="primary"
          [nzLoading]="infoLoading()"
          [disabled]="!pathInput().trim()"
          (click)="load()"
        >
          <span nz-icon nzType="search"></span>
          Load
        </button>
      } @else {
        <input
          nz-input
          class="path-input"
          [placeholder]="dirPlaceholder()"
          [ngModel]="dirInput()"
          (ngModelChange)="dirInput.set($event)"
          (keyup.enter)="listDir()"
        />

        <button
          nz-button
          nzType="primary"
          [nzLoading]="listLoading()"
          [disabled]="!dirInput().trim()"
          (click)="listDir()"
        >
          <span nz-icon nzType="folder-open"></span>
          List
        </button>
      }
    </div>

    <!-- Pick a bucket to browse its root (searchable dropdown) -->
    @if (inputMode() === "directory" && isAws() && buckets().length) {
      <div class="bucket-row">
        <span class="bucket-row-label">
          <span nz-icon nzType="database" nzTheme="outline"></span> Bucket
        </span>
        <nz-select
          nzSize="small"
          class="bucket-select"
          nzShowSearch
          nzAllowClear
          nzPlaceHolder="Select a bucket to browse"
          [ngModel]="currentBucket()"
          (ngModelChange)="$event && browseBucket($event)"
        >
          @for (b of buckets(); track b) {
            <nz-option [nzValue]="b" [nzLabel]="b"></nz-option>
          }
        </nz-select>
      </div>
    }

    @if (inputMode() === "file" && loadError(); as error) {
      <nz-alert
        class="load-error"
        nzType="error"
        nzShowIcon
        nzMessage="Could not open this file"
        [nzDescription]="error"
      ></nz-alert>
    }

    @if (inputMode() === "directory") {
      @if (listError(); as error) {
        <nz-alert
          class="load-error"
          nzType="error"
          nzShowIcon
          nzMessage="Could not list this directory"
          [nzDescription]="error"
        ></nz-alert>
      } @else if (listing(); as listing) {
        <div class="dir-listing">
          <div class="dir-current" [title]="listing.path">
            <span nz-icon nzType="folder-open" nzTheme="outline"></span>
            {{ listing.path }}
          </div>
          @if (parentDir()) {
            <button class="dir-entry" (click)="goUp()">
              <span nz-icon nzType="arrow-up" nzTheme="outline"></span>
              up one level
            </button>
          }
          @for (d of listing.dirs; track d) {
            <button class="dir-entry" (click)="openDir(d)">
              <span nz-icon nzType="folder" nzTheme="outline"></span>
              {{ d }}
            </button>
          }
          @for (f of listing.files; track f.name) {
            <button class="file-entry" (click)="openFile(f.name)">
              <span nz-icon nzType="file-text" nzTheme="outline"></span>
              <span class="file-entry-name">{{ f.name }}</span>
              <span class="muted">{{ formatBytes(f.size) }}</span>
            </button>
          }
          @if (!listing.dirs.length && !listing.files.length) {
            <span class="muted dir-empty">
              No sub-folders or SEG-Y files in this directory.
            </span>
          }
        </div>
      } @else if (isAws() && buckets().length) {
        <span class="muted dir-empty">
          Pick a bucket above, or type a path and press List.
        </span>
      }
    }
  </nz-card>

  @if (!loaded()) {
    @if (inputMode() === "file") {
      <nz-empty
        class="empty-state"
        nzNotFoundContent="Enter the path to a raw .sgy / .segy file and press Load."
      ></nz-empty>
    } @else {
      <nz-empty
        class="empty-state"
        nzNotFoundContent="Pick a bucket or enter a directory and press List, then click a SEG-Y file to open it."
      ></nz-empty>
    }
  } @else if (info(); as info) {
    <!-- ── Info bar (always visible above the tabs) ────────────── -->
    <div class="info-bar">
      <span class="info-file" [title]="info.path">
        <span nz-icon nzType="file-text" nzTheme="outline"></span>
        {{ fileName(info.path) }}
      </span>
      <nz-tag>{{ formatBytes(info.size) }}</nz-tag>
      @if (info.n_traces != null) {
        <nz-tag>{{ info.n_traces }} traces</nz-tag>
      }
      <nz-tag>ns {{ info.ns }}</nz-tag>
      <nz-tag>dt {{ formatDt(info.dt) }}</nz-tag>
      <nz-tag>{{ formatLabel(info.format) }}</nz-tag>
      <nz-tag [nzColor]="info.is_aws ? 'blue' : 'default'">
        {{ info.is_aws ? "S3" : "local" }}
      </nz-tag>
    </div>

    <nz-tabs [nzAnimated]="false" (nzSelectedIndexChange)="onTabChange($event)">
      <!-- ── Display: gather + bandpass filter ─────────────────── -->
      <nz-tab nzTitle="Display">
        <div class="gather-controls">
          <nz-radio-group
            nzSize="small"
            [ngModel]="gatherMode()"
            (ngModelChange)="gatherMode.set($event)"
          >
            <label nz-radio-button nzValue="field">By header</label>
            <label nz-radio-button nzValue="range">Trace range</label>
          </nz-radio-group>

          @if (gatherMode() === "field") {
            <nz-select
              nzSize="small"
              class="field-select"
              [ngModel]="scanField()"
              (ngModelChange)="scanField.set($event)"
            >
              @for (f of groupFields; track f) {
                <nz-option [nzValue]="f" [nzLabel]="f"></nz-option>
              }
            </nz-select>
            <nz-input-number
              nzSize="small"
              nzPlaceHolder="value"
              [nzStep]="1"
              [ngModel]="scanValue()"
              (ngModelChange)="scanValue.set($event)"
            ></nz-input-number>
          } @else {
            <label class="range-label">
              Start
              <nz-input-number
                nzSize="small"
                [nzMin]="0"
                [nzStep]="1"
                [ngModel]="traceStart()"
                (ngModelChange)="traceStart.set($event)"
              ></nz-input-number>
            </label>
            <label class="range-label">
              Count
              <nz-input-number
                nzSize="small"
                [nzMin]="1"
                [nzStep]="100"
                [ngModel]="traceCount()"
                (ngModelChange)="traceCount.set($event)"
              ></nz-input-number>
            </label>
          }

          <label class="range-label">
            Clip %
            <nz-input-number
              nzSize="small"
              [nzMin]="50"
              [nzMax]="100"
              [nzStep]="1"
              [ngModel]="clip()"
              (ngModelChange)="clip.set($event)"
            ></nz-input-number>
          </label>

          <button
            nz-button
            nzType="primary"
            nzSize="small"
            [nzLoading]="gatherLoading()"
            [disabled]="!canViewGather()"
            (click)="viewGather()"
          >
            <span nz-icon nzType="eye"></span>
            View gather
          </button>
        </div>

        @if (gatherMode() === "field" && groupingCapped()) {
          <nz-alert
            class="group-hint"
            nzType="info"
            nzShowIcon
            nzMessage="Very large file — only the first ~5M traces are scanned for grouping"
            nzDescription="Grouping by a header field still works, but the header scan is capped at ~5M traces, so a value that first appears past that window won't be found. To reach it, switch to “Trace range”: Start is a 0-based trace index, Count is how many traces to read."
          ></nz-alert>
        }

        <!-- Bandpass filter: per-leg slope + phase, applied on demand -->
        <div class="filter-row">
          <span class="filter-label">Bandpass</span>
          <div class="filter-leg">
            <span class="leg-title">Low cut</span>
            <label class="range-label">
              Hz
              <nz-input-number
                nzSize="small"
                nzPlaceHolder="—"
                [nzMin]="0"
                [nzMax]="maxCut()"
                [nzStep]="1"
                [ngModel]="lowcut()"
                (ngModelChange)="lowcut.set($event)"
              ></nz-input-number>
            </label>
            <label class="range-label">
              dB/oct
              <nz-select
                nzSize="small"
                class="order-select"
                [ngModel]="lowRoll()"
                (ngModelChange)="lowRoll.set($event)"
              >
                @for (r of rolls; track r) {
                  <nz-option [nzValue]="r" [nzLabel]="r"></nz-option>
                }
              </nz-select>
            </label>
            <nz-radio-group
              nzSize="small"
              [ngModel]="lowPhase()"
              (ngModelChange)="lowPhase.set($event)"
            >
              @for (p of phases; track p.value) {
                <label nz-radio-button [nzValue]="p.value">{{ p.label }}</label>
              }
            </nz-radio-group>
          </div>

          <div class="filter-leg">
            <span class="leg-title">High cut</span>
            <label class="range-label">
              Hz
              <nz-input-number
                nzSize="small"
                nzPlaceHolder="—"
                [nzMin]="0"
                [nzMax]="maxCut()"
                [nzStep]="1"
                [ngModel]="highcut()"
                (ngModelChange)="highcut.set($event)"
              ></nz-input-number>
            </label>
            <label class="range-label">
              dB/oct
              <nz-select
                nzSize="small"
                class="order-select"
                [ngModel]="highRoll()"
                (ngModelChange)="highRoll.set($event)"
              >
                @for (r of rolls; track r) {
                  <nz-option [nzValue]="r" [nzLabel]="r"></nz-option>
                }
              </nz-select>
            </label>
            <nz-radio-group
              nzSize="small"
              [ngModel]="highPhase()"
              (ngModelChange)="highPhase.set($event)"
            >
              @for (p of phases; track p.value) {
                <label nz-radio-button [nzValue]="p.value">{{ p.label }}</label>
              }
            </nz-radio-group>
          </div>

          <button
            nz-button
            nzType="primary"
            nzSize="small"
            [nzLoading]="gatherLoading()"
            [disabled]="!gather()"
            (click)="viewGather()"
          >
            <span nz-icon nzType="check"></span>
            Apply filters
          </button>
          @if (hasFilter()) {
            <button
              nz-button
              nzType="link"
              nzSize="small"
              (click)="lowcut.set(null); highcut.set(null)"
            >
              clear
            </button>
          }
          @if (nyquist()) {
            <span class="muted">Nyquist {{ nyquist() }} Hz</span>
          }
        </div>

        @if (gatherError(); as error) {
          <nz-alert
            class="gather-error"
            nzType="warning"
            nzShowIcon
            nzMessage="Could not load gather"
            [nzDescription]="error"
          ></nz-alert>
        }

        @if (gather(); as gather) {
          <div class="gather-result" id="segy-gather-capture">
            <div class="capture-toolbar omit-from-screenshot">
              @if (hasFilteredGather()) {
                <nz-radio-group
                  nzSize="small"
                  [ngModel]="showRaw()"
                  (ngModelChange)="showRaw.set($event)"
                >
                  <label nz-radio-button [nzValue]="false">Filtered</label>
                  <label nz-radio-button [nzValue]="true">Raw</label>
                </nz-radio-group>
                <span class="muted">flip to compare the bandpass impact</span>
              }
              <span class="capture-spacer"></span>
              <span class="muted hint">click a trace to read its headers</span>
              <button
                nz-button
                nzSize="small"
                nz-tooltip
                nzTooltipTitle="Download a PNG of this gather"
                (click)="screenshot('segy-gather-capture', 'gather')"
              >
                <span nz-icon nzType="camera"></span>
              </button>
            </div>
            <app-segy-gather-viewer
              [image]="gather.image"
              [meta]="gather.meta"
              [loading]="gatherLoading()"
              [selectedTraceIndex]="selectedTraceOrdinal()"
              (traceClick)="onTraceClick($event)"
            ></app-segy-gather-viewer>
          </div>

          <!-- Trace-click header readout -->
          @if (selectedTraceOrdinal() !== null) {
            <nz-card nzSize="small" class="trace-readout-card">
              <div class="trace-readout-head">
                <span class="trace-readout-title">
                  <span nz-icon nzType="aim"></span>
                  @if (selectedTraceHeader(); as th) {
                    Trace {{ th.trace }} (gather position {{ selectedTraceOrdinal()! + 1 }})
                  } @else {
                    Trace headers
                  }
                </span>
                <button
                  nz-button
                  nzType="text"
                  nzSize="small"
                  (click)="clearSelectedTrace()"
                >
                  <span nz-icon nzType="close"></span>
                </button>
              </div>

              @if (traceHeaderLoadingOne()) {
                <div class="section-loading">
                  <nz-spin nzTip="Reading trace header..."></nz-spin>
                </div>
              } @else if (traceHeaderErrorOne(); as error) {
                <nz-alert
                  nzType="error"
                  nzShowIcon
                  nzMessage="Could not read this trace's header"
                  [nzDescription]="error"
                ></nz-alert>
              } @else if (selectedTraceHeader()) {
                <div class="trace-readout-grid">
                  @for (row of selectedTraceRows(); track row.name) {
                    <div class="trace-readout-row">
                      <span class="tr-name">{{ row.name }}</span>
                      @if (row.bytes) {
                        <span class="tr-bytes">{{ byteLabel(row.bytes) }}</span>
                      }
                      <span class="tr-value">{{ row.value }}</span>
                    </div>
                  }
                </div>
              }
            </nz-card>
          }
        } @else {
          <nz-empty
            class="tab-empty"
            nzNotFoundContent="Pick a gather (by header value or trace range) and press View gather."
          ></nz-empty>
        }
      </nz-tab>

      <!-- ── Spectrum of the displayed gather ──────────────────── -->
      <nz-tab nzTitle="Spectrum">
        <div class="spectrum-bar">
          <span class="muted">Amplitude spectrum of the displayed gather</span>
          <span class="capture-spacer"></span>
          <nz-switch
            nzSize="small"
            nzCheckedChildren="log"
            nzUnCheckedChildren="lin"
            [ngModel]="logScale()"
            (ngModelChange)="logScale.set($event)"
          ></nz-switch>
          @if (spectrumOptions()) {
            <button
              nz-button
              nzSize="small"
              nz-tooltip
              nzTooltipTitle="Download a PNG of this spectrum"
              (click)="screenshot('segy-spectrum-capture', 'spectrum')"
            >
              <span nz-icon nzType="camera"></span>
            </button>
          }
        </div>
        @if (spectrumError(); as error) {
          <nz-alert
            nzType="warning"
            nzShowIcon
            nzMessage="Could not load spectrum"
            [nzDescription]="error"
          ></nz-alert>
        } @else if (spectrumOptions(); as options) {
          <div id="segy-spectrum-capture">
            <div echarts [options]="options" class="spectrum-chart"></div>
          </div>
        } @else {
          <nz-empty
            class="tab-empty"
            nzNotFoundContent="View a gather on the Display tab to see its spectrum."
          ></nz-empty>
        }
      </nz-tab>

      <!-- ── Geometry (source/receiver scatter) ────────────────── -->
      <nz-tab nzTitle="Geometry">
        <div class="geometry-controls">
          <button
            nz-button
            nzSize="small"
            [nzLoading]="geometryLoading()"
            (click)="loadGeometry()"
          >
            <span nz-icon nzType="reload"></span>
            {{ geometry() ? "Reload" : "Plot" }} source / receiver geometry
          </button>
          @if (geometry(); as geo) {
            <span class="geo-counts">
              {{ geo.n_sources }} sources · {{ geo.n_receivers }} receivers ·
              {{ geo.n_traces }} traces
              @if (geo.sampled) {
                · <span class="muted">sampled {{ geo.scanned_traces }} traces</span>
              }
            </span>
            <span class="capture-spacer"></span>
            <button
              nz-button
              nzSize="small"
              nz-tooltip
              nzTooltipTitle="Download a PNG of this map"
              (click)="screenshot('segy-geometry-capture', 'geometry')"
            >
              <span nz-icon nzType="camera"></span>
            </button>
          }
        </div>
        @if (geometryError(); as error) {
          <nz-alert
            nzType="error"
            nzShowIcon
            nzMessage="Failed to scan geometry from the trace headers"
            [nzDescription]="error"
          ></nz-alert>
        } @else if (geometryOptions(); as options) {
          <div id="segy-geometry-capture">
            <div echarts [options]="options" class="geometry-chart"></div>
          </div>
        } @else if (!geometryLoading()) {
          <nz-empty
            class="tab-empty"
            nzNotFoundContent="No source/receiver coordinates found in the trace headers."
          ></nz-empty>
        }
      </nz-tab>

      <!-- ── Headers: EBCDIC + binary + trace table ────────────── -->
      <nz-tab nzTitle="Headers">
        <nz-card nzSize="small" nzTitle="Text Header (EBCDIC)" class="detail-card">
          @if (headersLoading()) {
            <div class="section-loading"><nz-spin nzTip="Loading headers..."></nz-spin></div>
          } @else if (headersError(); as error) {
            <nz-alert
              nzType="error"
              nzShowIcon
              nzMessage="Failed to load file headers"
              [nzDescription]="error"
            ></nz-alert>
          } @else if (headers(); as headers) {
            <pre class="text-header">{{ headers.text_header }}</pre>
            <div class="encoding-note">decoded as {{ headers.encoding }}</div>
          }
        </nz-card>

        @if (headers()) {
          <nz-card nzSize="small" nzTitle="Binary Header" class="detail-card">
            <nz-table
              #binaryTable
              [nzData]="binaryHeaderEntries()"
              nzSize="small"
              [nzFrontPagination]="false"
              [nzShowPagination]="false"
              [nzScroll]="{ y: '260px' }"
            >
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                @for (entry of binaryTable.data; track entry.field) {
                  <tr>
                    <td class="field-name">{{ entry.field }}</td>
                    <td>{{ entry.value }}</td>
                  </tr>
                }
              </tbody>
            </nz-table>
          </nz-card>
        }

        <nz-card nzSize="small" nzTitle="Trace Headers" class="detail-card">
          @if (traceHeadersError(); as error) {
            <nz-alert
              nzType="error"
              nzShowIcon
              nzMessage="Failed to load trace headers"
              [nzDescription]="error"
            ></nz-alert>
          } @else {
            <!-- Column chooser + byte-location toggle -->
            <div class="traceheader-controls">
              <span class="filter-label">Columns</span>
              <nz-select
                nzMode="multiple"
                nzSize="small"
                class="column-select"
                nzPlaceHolder="choose header fields"
                [nzMaxTagCount]="8"
                [nzAllowClear]="true"
                [ngModel]="selectedColumns()"
                (ngModelChange)="selectedColumns.set($event)"
              >
                @for (col of availableColumns(); track col) {
                  <nz-option
                    [nzValue]="col"
                    [nzLabel]="col + ' (' + byteLabel(fieldBytes()[col]) + ')'"
                  ></nz-option>
                }
              </nz-select>
              <button
                nz-button
                nzType="link"
                nzSize="small"
                (click)="selectDefaultColumns()"
              >
                Defaults
              </button>
              <button
                nz-button
                nzType="link"
                nzSize="small"
                (click)="selectAllColumns()"
              >
                All fields
              </button>
              <nz-switch
                nzSize="small"
                nzCheckedChildren="bytes"
                nzUnCheckedChildren="bytes"
                [ngModel]="showByteLocations()"
                (ngModelChange)="showByteLocations.set($event)"
              ></nz-switch>
            </div>

            <nz-table
              #traceTable
              [nzData]="displayedRows()"
              nzSize="small"
              [nzFrontPagination]="false"
              [nzTotal]="traceHeadersTotal()"
              [nzPageIndex]="tracePage()"
              [nzPageSize]="tracePageSize"
              [nzLoading]="traceHeadersLoading()"
              [nzShowSizeChanger]="false"
              [nzScroll]="{ x: 'max-content', y: '340px' }"
              (nzPageIndexChange)="onTraceHeaderPageChange($event)"
            >
              <thead>
                <tr>
                  <th nzLeft nzWidth="80px">Trace</th>
                  @for (col of displayedColumnInfo(); track col.name) {
                    <th>
                      <div class="th-name">{{ col.name }}</div>
                      @if (showByteLocations() && col.bytes) {
                        <div class="th-bytes">{{ byteLabel(col.bytes) }}</div>
                      }
                    </th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of traceTable.data; track $index) {
                  <tr>
                    <td nzLeft class="trace-no">
                      {{ traceHeaderOffset() + $index + 1 }}
                    </td>
                    @for (value of row; track $index) {
                      <td>{{ value }}</td>
                    }
                  </tr>
                }
              </tbody>
            </nz-table>
          }
        </nz-card>
      </nz-tab>

      <!-- ── Header plots (cross-plot any field, incl. custom bytes) ── -->
      <nz-tab nzTitle="Header plots">
        <div class="headerplot-controls">
          <label class="range-label">
            X
            <nz-select
              nzSize="small"
              class="field-select"
              [ngModel]="xAxisField()"
              (ngModelChange)="xAxisField.set($event)"
            >
              @for (f of xAxisOptions(); track f) {
                <nz-option [nzValue]="f" [nzLabel]="axisLabel(f)"></nz-option>
              }
            </nz-select>
          </label>
          <label class="range-label">
            Y
            <nz-select
              nzSize="small"
              class="field-select"
              [ngModel]="yAxisField()"
              (ngModelChange)="yAxisField.set($event)"
            >
              @for (f of yAxisOptions(); track f) {
                <nz-option [nzValue]="f" [nzLabel]="axisLabel(f)"></nz-option>
              }
            </nz-select>
          </label>
          <label
            class="range-label"
            nz-tooltip
            nzTooltipTitle="How many traces to sample, spread evenly across the file"
          >
            Max points
            <nz-input-number
              nzSize="small"
              class="density-input"
              [nzMin]="100"
              [nzStep]="1000"
              [ngModel]="headerPlotMaxPoints()"
              (ngModelChange)="headerPlotMaxPoints.set($event)"
            ></nz-input-number>
          </label>
          <button
            nz-button
            nzSize="small"
            [nzLoading]="headerPlotLoading()"
            (click)="loadHeaderPlot()"
          >
            <span nz-icon nzType="reload"></span>
            {{ headerTable() ? "Reload" : "Scan" }} headers
          </button>
          @if (headerTable(); as t) {
            <span class="muted">
              {{ t.trace.length }} points
              @if (t.sampled) {
                · sampled {{ t.scanned_traces }} of {{ t.n_traces }} traces
              }
            </span>
            <span class="capture-spacer"></span>
            <button
              nz-button
              nzSize="small"
              nz-tooltip
              nzTooltipTitle="Download a PNG of this plot"
              (click)="screenshot('segy-headerplot-capture', 'header-plot')"
            >
              <span nz-icon nzType="camera"></span>
            </button>
          }
        </div>

        <!-- Custom byte/format fields: read a non-standard header -->
        <div class="custom-field-row">
          <span class="filter-label">Custom field</span>
          <label class="range-label">
            byte
            <nz-input-number
              nzSize="small"
              nzPlaceHolder="e.g. 197"
              [nzMin]="1"
              [nzMax]="240"
              [nzStep]="1"
              [ngModel]="customByteDraft()"
              (ngModelChange)="customByteDraft.set($event)"
            ></nz-input-number>
          </label>
          <nz-select
            nzSize="small"
            class="format-select"
            [ngModel]="customFormatDraft()"
            (ngModelChange)="customFormatDraft.set($event)"
          >
            @for (fmt of headerFormats; track fmt.value) {
              <nz-option [nzValue]="fmt.value" [nzLabel]="fmt.label"></nz-option>
            }
          </nz-select>
          <button nz-button nzSize="small" nzType="dashed" (click)="addCustomField()">
            <span nz-icon nzType="plus"></span>
            Add field
          </button>
          @for (f of customFields(); track f.byte + ':' + f.format) {
            <nz-tag nzMode="closeable" (nzOnClose)="removeCustomField(f)">
              byte {{ f.byte }} · {{ f.format }}
            </nz-tag>
          }
        </div>

        @if (headerPlotError(); as error) {
          <nz-alert
            nzType="error"
            nzShowIcon
            nzMessage="Failed to scan trace headers"
            [nzDescription]="error"
          ></nz-alert>
        } @else if (headerPlotOptions(); as options) {
          <div id="segy-headerplot-capture">
            <div echarts [options]="options" class="headerplot-chart"></div>
          </div>
        } @else if (!headerPlotLoading()) {
          <nz-empty
            class="tab-empty"
            nzNotFoundContent="Scan the trace headers, then choose fields to plot. Add a custom byte/format field to plot a non-standard header."
          ></nz-empty>
        }
      </nz-tab>

      <!-- ── Model: survey geometry of a 3D SEG-Y volume ───────── -->
      <nz-tab nzTitle="Model">
        <div class="geometry-controls">
          <button
            nz-button
            nzSize="small"
            [nzLoading]="modelLoading()"
            (click)="loadModelGeometry()"
          >
            <span nz-icon nzType="reload"></span>
            {{ modelGeometry() ? "Reload" : "Scan" }} model geometry
          </button>
          @if (modelGeometry(); as m) {
            <span class="geo-counts">
              {{ m.n_traces }} traces
              @if (m.sampled) {
                · <span class="muted">sampled {{ m.scanned_traces }}</span>
              }
              · coords from {{ m.coordinate_source }}
            </span>
            <span class="capture-spacer"></span>
            <button
              nz-button
              nzSize="small"
              nz-tooltip
              nzTooltipTitle="Download a PNG of the model footprint"
              (click)="screenshot('segy-model-capture', 'model-geometry')"
            >
              <span nz-icon nzType="camera"></span>
            </button>
          }
        </div>

        @if (modelError(); as error) {
          <nz-alert
            nzType="error"
            nzShowIcon
            nzMessage="Could not derive model geometry"
            [nzDescription]="error"
          ></nz-alert>
        } @else if (modelGeometry(); as m) {
          <div id="segy-model-capture" class="model-capture">
            <div class="model-layout">
              <nz-card nzSize="small" nzTitle="Survey geometry" class="model-readout">
                @if (m.grid; as grid) {
                  <div class="model-row">
                    <span class="m-name">Rotation</span>
                    <span class="m-value">{{ formatNum(grid.rotation, 2) }}°
                      <span class="muted">cw from north</span></span>
                  </div>
                  @if (grid.origin) {
                    <div class="model-row">
                      <span class="m-name">Origin (X, Y)</span>
                      <span class="m-value">
                        {{ formatNum(grid.origin[0]) }}, {{ formatNum(grid.origin[1]) }}
                      </span>
                    </div>
                  }
                  @if (grid.bin_inline != null) {
                    <div class="model-row">
                      <span class="m-name">Bin (il × xl)</span>
                      <span class="m-value">
                        {{ formatNum(grid.bin_inline, 2) }} ×
                        {{ formatNum(grid.bin_crossline, 2) }}
                      </span>
                    </div>
                  }
                  @if (grid.inline_range) {
                    <div class="model-row">
                      <span class="m-name">Inlines</span>
                      <span class="m-value">
                        {{ grid.inline_range[0] }}–{{ grid.inline_range[1] }}
                        ({{ grid.n_inlines }}, inc {{ grid.inline_increment }})
                      </span>
                    </div>
                    <div class="model-row">
                      <span class="m-name">Crosslines</span>
                      <span class="m-value">
                        {{ grid.crossline_range![0] }}–{{ grid.crossline_range![1] }}
                        ({{ grid.n_crosslines }}, inc {{ grid.crossline_increment }})
                      </span>
                    </div>
                  }
                  @if (grid.inline_azimuth != null) {
                    <div class="model-row">
                      <span class="m-name">Azimuth (il / xl)</span>
                      <span class="m-value">
                        {{ formatNum(grid.inline_azimuth, 1) }}° /
                        {{ formatNum(grid.crossline_azimuth, 1) }}°
                      </span>
                    </div>
                  }
                }
                @if (m.bbox; as b) {
                  <div class="model-row">
                    <span class="m-name">Bounding box X</span>
                    <span class="m-value">{{ formatNum(b.min_x) }} … {{ formatNum(b.max_x) }}</span>
                  </div>
                  <div class="model-row">
                    <span class="m-name">Bounding box Y</span>
                    <span class="m-value">{{ formatNum(b.min_y) }} … {{ formatNum(b.max_y) }}</span>
                  </div>
                  <div class="model-row">
                    <span class="m-name">Extent (W × H)</span>
                    <span class="m-value">{{ formatNum(b.width) }} × {{ formatNum(b.height) }}</span>
                  </div>
                }
                <div class="model-row">
                  <span class="m-name">Depth / time axis</span>
                  <span class="m-value">
                    {{ m.z_axis.n_samples }} samples
                    @if (m.z_axis.total != null) {
                      · total {{ formatNum(m.z_axis.total, 3) }}
                    }
                  </span>
                </div>
                @if (m.grid; as grid) {
                  <div class="model-method muted">{{ grid.method }}</div>
                }
              </nz-card>

              @if (modelMapOptions(); as options) {
                <div echarts [options]="options" class="model-map"></div>
              }
            </div>
          </div>
          @if (!m.has_inline_crossline) {
            <nz-alert
              class="group-hint"
              nzType="info"
              nzShowIcon
              nzMessage="No inline/crossline headers found"
              nzDescription="Bin spacing and the rotated grid corners need populated inline (189–192) / crossline (193–196) headers. Without them the rotation + extent are estimated from the coordinate cloud's principal axes."
            ></nz-alert>
          }
        } @else if (!modelLoading()) {
          <nz-empty
            class="tab-empty"
            nzNotFoundContent="Scan the model geometry to derive its origin, bounding box, bin spacing and rotation from the trace headers."
          ></nz-empty>
        }
      </nz-tab>
    </nz-tabs>
  }
</div>
